import client from '@/api/client';

export interface PipelineOverview {
  total: number;
  in_progress: number;
  live: number;
  expired: number;
  fordshare_coverage: number;
  fordshare_total: number;
  gone_live_this_week: number;
  avg_days_to_live: number | null;
  completion_rate: number | null;
}

export interface StatusBreakdownItem {
  status: string;
  label: string;
  count: number;
}

export interface SLASummary {
  on_track: number;
  warning: number;
  breached: number;
  not_applicable: number;
  by_phase: {
    creative_localisation: { on_track: number; warning: number; breached: number };
    media_activation: { on_track: number; warning: number; breached: number };
  };
}

export interface GoLiveTrendItem {
  week: string;
  count: number;
}

export interface PipelineData {
  overview: PipelineOverview;
  status_breakdown: StatusBreakdownItem[];
  sla_summary: SLASummary;
  go_live_trend: GoLiveTrendItem[];
  market_breakdown: MarketBreakdownItem[];
}

export interface BreakdownItem {
  name: string;
  count: number;
}

export interface MarketBreakdownItem {
  market: string;
  count: number;
}

export interface PlatformFormatItem {
  platform: string;
  total: number;
  formats: BreakdownItem[];
}

export interface BreakdownData {
  market_breakdown: MarketBreakdownItem[];
  platform_breakdown: BreakdownItem[];
  format_breakdown: BreakdownItem[];
  platform_format_breakdown: PlatformFormatItem[];
  nameplate_breakdown: BreakdownItem[];
}

export async function fetchBreakdownData(params: {
  campaign_status?: string;
  market?: string;
  quarter?: string;
  year?: string;
  business_unit?: string;
}): Promise<BreakdownData> {
  const response = await client.get('/dashboard/user/breakdown', { params });
  return response.data;
}

export interface TimelineOverview {
  gone_live_this_week: number;
  gone_live_this_month: number;
  total_events: number;
  avg_days_to_live: number | null;
  live_sample_size: number;
}

export interface WeeklyTrendItem {
  week: string;
  count: number;
}

export interface TimelineData {
  overview: TimelineOverview;
  go_live_trend: WeeklyTrendItem[];
  activity_trend: WeeklyTrendItem[];
}

export async function fetchTimelineData(params: {
  campaign_status?: string;
  market?: string;
}): Promise<TimelineData> {
  const response = await client.get('/dashboard/user/timeline', { params });
  return response.data;
}

export interface PlansOverview {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface ImportHistoryItem {
  date: string | null;
  rows_processed: number;
  campaigns_created: number;
  campaigns_updated: number;
  errors: number;
  status: string;
}

export interface PlansData {
  overview: PlansOverview;
  by_market: BreakdownItem[];
  by_channel: BreakdownItem[];
  import_history: ImportHistoryItem[];
}

export async function fetchPlansData(): Promise<PlansData> {
  const response = await client.get('/dashboard/user/plans');
  return response.data;
}

export async function fetchPipelineData(params: {
  campaign_status?: string;
  market?: string;
  quarter?: string;
  year?: string;
  business_unit?: string;
}): Promise<PipelineData> {
  const response = await client.get('/dashboard/user/pipeline', { params });
  return response.data;
}
